import { useCallback } from 'react';
import { useRouter } from 'next/router';
import { getAccessToken, isTokenValid, setTokens, getUserDataFromToken, saveUserData, type UserData } from '../../services/auth';
import { setSession, reAuthenticateWithPassword } from '../../services/auth';
import { authFetch } from '../../services/auth';

const AUTH_URL = process.env.NEXT_PUBLIC_BASE_URL || '';

export interface PasswordResult {
  success: boolean;
  message?: string;
  error?: string;
  errorCode?: string;
  needsReauth?: boolean;
  recommendReauth?: boolean;
}

interface UsePasswordActionsProps {
  user: UserData | null;
  setUser: (user: UserData | null) => void;
  setLoading: (loading: boolean) => void;
  clearAuthData: () => void;
  refreshUserData: (options?: { forceTokenRefresh?: boolean }) => Promise<boolean>;
}

async function executeWithRetry<T>(
  action: () => Promise<T>,
  refreshFn: (options?: { forceTokenRefresh?: boolean }) => Promise<boolean>,
  errorMessage: string
): Promise<T> {
  try {
    return await action();
  } catch (error) {
    if (error instanceof Error && (error.message.includes('invalid JWT') || error.message.includes('malformed'))) {
      await refreshFn({ forceTokenRefresh: true });
      try {
        return await action();
      } catch (retryError) {
        throw new Error(retryError instanceof Error ? retryError.message : errorMessage);
      }
    }
    throw error;
  }
}

export function usePasswordActions({ user, setUser, setLoading, clearAuthData, refreshUserData }: UsePasswordActionsProps) {
  const router = useRouter();

  const requestPasswordReset = useCallback(
    async (email: string): Promise<PasswordResult> => {
      setLoading(true);
      try {
        const response = await fetch(`${AUTH_URL}/api/auth/forgot-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || errorData.error || 'Failed to send reset email');
        }

        return { success: true };
      } catch (error) {
        return { success: false, error: error instanceof Error ? error.message : 'Failed to send reset email' };
      } finally {
        setLoading(false);
      }
    },
    [setLoading]
  );

  const verifyResetToken = useCallback(
    async (email: string, token: string): Promise<PasswordResult> => {
      setLoading(true);
      let failureCode: string | undefined;
      try {
        const response = await fetch(`${AUTH_URL}/api/auth/verify-reset-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, token }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          // The API's machine-readable code is what callers phrase their copy
          // from; the message is kept as the fallback for older responses.
          failureCode = errorData.code;
          throw new Error(errorData.message || errorData.error || 'Invalid or expired verification code');
        }

        const data = await response.json();
        const session = data?.session;

        if (session?.access_token) {
          const { access_token, refresh_token } = session;
          setTokens(access_token, refresh_token);
          await setSession(access_token, refresh_token);
          setUser(getUserDataFromToken(access_token));
        } else {
          clearAuthData();
        }

        return { success: true };
      } catch (error) {
        clearAuthData();
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Verification failed',
          errorCode: failureCode,
        };
      } finally {
        setLoading(false);
      }
    },
    [setUser, setLoading, clearAuthData]
  );

  const resetPassword = useCallback(
    async (password: string): Promise<PasswordResult> => {
      setLoading(true);
      const token = getAccessToken();

      if (!token || !isTokenValid()) {
        setLoading(false);
        clearAuthData();
        router.push('/forget-password?status=verification-expired');
        return { success: false, error: 'Authentication token is missing or expired. Please verify your code again.' };
      }

      try {
        await authFetch(`${AUTH_URL}/api/auth/reset-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ new_password: password }),
        });

        clearAuthData();
        router.push('/sign-in?status=password-reset-success');
        return { success: true };
      } catch (error) {
        if (error instanceof Error && (error.message.includes('Unauthorized') || error.message.includes('401'))) {
          clearAuthData();
          router.push('/forget-password?status=reset-error');
        }
        return { success: false, error: error instanceof Error ? error.message : 'Failed to reset password' };
      } finally {
        setLoading(false);
      }
    },
    [setLoading, clearAuthData, router]
  );

  const updatePassword = useCallback(
    async (currentPassword: string, newPassword: string): Promise<PasswordResult> => {
      setLoading(true);

      try {
        const isFirstTimePasswordSet = user?.provider !== 'email' && user?.passwordSet !== true;

        if (!newPassword) throw new Error('New password is required');
        if (newPassword.length < 8) throw new Error('New password must be at least 8 characters long');

        if (!isFirstTimePasswordSet) {
          if (!currentPassword) throw new Error('Current password is required');
          if (currentPassword === newPassword) throw new Error('New password cannot be the same as current password');
        }

        await refreshUserData({ forceTokenRefresh: true });

        const performUpdate = () =>
          authFetch<{ message: string; data?: { needsSessionRefresh?: boolean; isFirstPassword?: boolean } }>(
            `${AUTH_URL}/api/auth/update-password`,
            {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ currentPassword: currentPassword || '', newPassword }),
            }
          );

        const response = await executeWithRetry(performUpdate, refreshUserData, 'Failed to update password');

        if (response?.data?.needsSessionRefresh && user?.email) {
          const reauthResult = await handlePostPasswordChangeReauth(user.email, newPassword, response.data.isFirstPassword, setUser, clearAuthData);
          if (reauthResult.needsReauth) {
            return { success: true, message: response.message + ' Please sign in again with your new password.', needsReauth: true };
          }
        }

        return { success: true, message: response?.message || 'Password updated successfully', recommendReauth: false };
      } catch (error) {
        return { success: false, error: error instanceof Error ? error.message : 'Failed to update password' };
      } finally {
        setLoading(false);
      }
    },
    [user, setUser, setLoading, clearAuthData, refreshUserData]
  );

  const updateEmail = useCallback(
    async (newEmail: string, currentPassword: string): Promise<PasswordResult> => {
      setLoading(true);

      try {
        if (!newEmail || !currentPassword) throw new Error('New email and current password are required');

        await refreshUserData({ forceTokenRefresh: true });

        const performUpdate = () =>
          authFetch<{ message: string }>(`${AUTH_URL}/api/auth/update-email`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ newEmail: newEmail.trim(), currentPassword }),
          });

        const response = await executeWithRetry(performUpdate, refreshUserData, 'Failed to update email');
        return { success: true, message: response?.message };
      } catch (error) {
        return { success: false, error: error instanceof Error ? error.message : 'Failed to update email' };
      } finally {
        setLoading(false);
      }
    },
    [setLoading, refreshUserData]
  );

  return { requestPasswordReset, verifyResetToken, resetPassword, updatePassword, updateEmail };
}

async function handlePostPasswordChangeReauth(
  email: string,
  newPassword: string,
  isFirstPassword: boolean | undefined,
  setUser: (user: UserData | null) => void,
  clearAuthData: () => void
): Promise<{ needsReauth: boolean }> {
  try {
    const result = await reAuthenticateWithPassword(email, newPassword);

    if (!result.success) {
      clearAuthData();
      return { needsReauth: true };
    }

    if (result.userData) {
      if (isFirstPassword) result.userData.passwordSet = true;
      saveUserData(result.userData);
      setUser(result.userData);
    }

    await new Promise((resolve) => setTimeout(resolve, 100));
    return { needsReauth: false };
  } catch {
    clearAuthData();
    return { needsReauth: true };
  }
}
